<template>
    <div class="col-span-12 lg:col-span-10 flex flex-col w-full justify-center gap-2 md:gap-2">
        <page-header>
            Contacts
        </page-header>

        <campaign-contacts-filters v-model="fetchParams"
            @reset="resetFilters"
            @apply="applyFilters"
            @bulkAdd="addContactsToCampaign"
            @bulkRemove="removeContactsFromCampaign"
            :disableButtons="disableButtons"
        ></campaign-contacts-filters>

        <empty-state
            v-if="!loading && list.length === 0 && !isEqual(defaultFilters(), fetchParams)"
            @reset="resetFilters"
        />

        <vx-box v-if="!loading && list.length === 0 && isEqual(defaultFilters(), fetchParams)" color="info"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
            There are no contacts for this campaign.
        </vx-box>

        <vx-table v-if="list.length"
            class="xl:grid-cols-[repeat(7,auto)]"
            rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div @click.prevent="toggleSelectAll" data-test="select-all">
                    <vx-checkbox :modelValue="selectedAll"></vx-checkbox>
                </div>
                <div class="xl:col-span-2">
                    Name
                </div>
                <div>
                    Phone
                </div>
                <div>
                    Email
                </div>
                <div>
                    Campaign Sends
                </div>
            </vx-table-row>

            <campaign-contact-row v-for="(contact, index) in list"
                :key="list[index].id"
                :modelValue="list[index]"
                :campaignId="props.campaignId"
                :selected="selectedContacts.includes(contact.id)"
                @update:selected="toggleSelected(contact.id)"
                :pending="pendingContacts.includes(contact.id)"
                @add="addContactsToCampaign([contact.id])"
                @remove="removeContactsFromCampaign([contact.id])"
            >
            </campaign-contact-row>

        </vx-table>

        <div v-if="list.length" class="mt-8 flex justify-between w-full gap-10">
            <vx-button v-if="fetchParams.cursor" @click="loadData()" data-test="load-more-btn" color="muted-dark"
                size="xl"
                class="grow-0" :loading="loading">
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
        </div>
    </div>
</template>

<script setup>
import {
    VxButton,
    VxTable,
    VxTableRow,
    VxBox,
    VxCheckbox,
} from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { computed, onMounted, ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { useCursorPagination } from '~/composables/useCursorPagination';
import CampaignContactRow from './CampaignContactRow.vue';
import CampaignContactsFilters from './CampaignContactsFilters.vue';
import { campaignsClient, contactsClient } from '~/services';
import { isEqual } from '~/utils/helpers';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { useCampaignDetails } from '../useCampaignDetails';

const toasted = useToasted();

const props = defineProps({
    campaignId: {
        type: Number,
        required: true,
    }
});

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();
const { campaign } = useCampaignDetails();

const teamId = Spark.state.currentTeam.id;

const selectedContacts = ref([]);
const pendingContacts = ref([]);

const contactsIds = computed(() => (list.value.map((contact) => contact.id)));
const selectedAll = computed(() => (contactsIds.value.every((id) => selectedContacts.value.includes(id))));
const disableButtons = computed(() => (selectedContacts.value.length <= 0));

const defaultFilters = () => {
    return {
        cursor: null,
        per_page: 10,
        sort_field: 'id',
        sort_dir: 'asc',
        'filter[search]': '',
    }
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    selectedContacts.value = [];

    loadData();
}

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    selectedContacts.value = [];

    loadContacts();
};

onMounted(() => {
    resetFilters();
});

const loadContacts = () => {
    fetchRecords(campaignsClient.campaignsAllContacts(teamId, props.campaignId, { ...fetchParams.value, minimal: 1 }));
};

const searchContacts = () => {
    fetchRecords(contactsClient.searchContacts(teamId, {
        cursor: fetchParams.value.cursor,
        per_page: 10,
        'filter[search]': fetchParams.value['filter[search]'],
        'include': 'campaigns',
    }))
};

const loadData = () => {
    if (fetchParams.value['filter[search]'] !== '') {
        searchContacts();
    } else {
        loadContacts();
    }
};

const toggleSelected = (id) => {
    if (selectedContacts.value.some((contactId) => contactId === id)) {
        selectedContacts.value = selectedContacts.value.filter((contactId) => contactId !== id);
    } else {
        selectedContacts.value.push(id);
    }
};

const toggleSelectAll = () => {
    if (selectedAll.value === true) {
        selectedContacts.value = [];
    } else {
        selectedContacts.value = contactsIds.value;
    }
};

const addContactsToCampaign = async (ids = []) => {
    try {
        if (ids.length === 0) {
            ids = [...selectedContacts.value];
        }

        pendingContacts.value.push(...ids);

        const response = await campaignsClient.addContactsToCampaign(teamId, props.campaignId, ids)

        response.data.forEach((contactId) => {
            const contactIndex = list.value.findIndex((contact) => contact.id === contactId);

            if (contactIndex >= 0) {
                const campaignIndex = list.value[contactIndex].campaigns.findIndex((contactCampaign) => contactCampaign.id === props.campaignId);

                if (campaignIndex === -1) {
                    list.value[contactIndex].campaigns.push({...campaign.value});
                }
            }
        });
    } catch (error) {
        console.error(error);
        if (
            !error?.response?.status ||
            error?.response?.status >= HTTP_INTERNAL_SERVER_ERROR ||
            error?.response.status === HTTP_NOT_FOUND
        ) {
            toasted?.global.platform_error();
        } else {
            toasted?.error(error.response?.data?.message || 'Something went wrong.');
        }
    } finally {
        clearPendingContacts(ids);
        selectedContacts.value = [];
    }
};

const removeContactsFromCampaign = async (ids = []) => {
    try {
        if (ids.length === 0) {
            ids = [...selectedContacts.value];
        }

        pendingContacts.value.push(...ids);

        const response = await campaignsClient.campaignsDeleteContacts(teamId, props.campaignId, ids);

        response.data.forEach(contactId => {
            const contactIndex = list.value.findIndex((contact) => contact.id === contactId);

            if (contactIndex >= 0) {
                //remove the campaign from the contact
                list.value[contactIndex].campaigns =
                    list.value[contactIndex].campaigns.filter((contactCampaign) =>
                        contactCampaign.id !== props.campaignId
                    );
            }
        });
    } catch (error) {
        console.error(error);
        if (
            !error?.response?.status ||
            error?.response?.status >= HTTP_INTERNAL_SERVER_ERROR ||
            error?.response.status === HTTP_NOT_FOUND
        ) {
            toasted?.global.platform_error();
        } else {
            toasted?.error(error.response?.data?.message || 'Something went wrong.');
        }
    } finally {
        clearPendingContacts(ids);
        selectedContacts.value = [];
    }
};

const clearPendingContacts = (ids) => {
    pendingContacts.value = pendingContacts.value.filter((contactId) => !ids.some(id => id === contactId));
};
</script>
